import { COLORS } from '@/config/colors';
import { css, fillOutlined, circle, shade } from './draw-utils';
import type { ProceduralDef } from '../types';

/** Molten lava pool — touching it burns the ship (docs/12 E4). 2 bubble frames. */
export const envLavaPool: ProceduralDef = {
  key: 'env/lava-pool',
  width: 192,
  height: 192,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 2,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const R = c - 8;
    // Crusted rim
    ctx.beginPath();
    const lumps = 10;
    for (let i = 0; i <= lumps; i++) {
      const a = (Math.PI * 2 * i) / lumps;
      const rr = R * (0.93 + 0.07 * Math.sin(i * 2.4 + frame));
      const x = c + Math.cos(a) * rr;
      const y = c + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    fillOutlined(ctx, shade(COLORS.groundMagmaDark, 0.6), 6);
    // Molten core gradient
    const grad = ctx.createRadialGradient(c, c, R * 0.12, c, c, R * 0.88);
    grad.addColorStop(0, 'rgba(255, 235, 140, 1)');
    grad.addColorStop(0.45, 'rgba(255, 122, 48, 1)');
    grad.addColorStop(1, 'rgba(170, 40, 30, 0.95)');
    circle(ctx, c, c, R * 0.86);
    ctx.fillStyle = grad;
    ctx.fill();
    // Bubbles alternate per frame so the pool reads as boiling
    ctx.fillStyle = 'rgba(255, 230, 150, 0.9)';
    const bubbles = frame === 0
      ? [[-0.3, -0.15, 7], [0.25, 0.2, 5], [-0.05, 0.42, 4]] as const
      : [[0.32, -0.25, 6], [-0.22, 0.3, 5], [0.05, -0.05, 4]] as const;
    for (const [bx, by, br] of bubbles) {
      circle(ctx, c + bx * R, c + by * R, br);
      ctx.fill();
    }
    void h;
  },
};

/** Chasm pit — dark hole the ship cannot cross; bullets fly over it. */
export const envPit: ProceduralDef = {
  key: 'env/pit',
  width: 192,
  height: 192,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 1,
  draw(ctx, w, h) {
    const c = w / 2;
    const R = c - 8;
    // Rim — slightly lighter broken edge
    ctx.beginPath();
    const lumps = 11;
    for (let i = 0; i <= lumps; i++) {
      const a = (Math.PI * 2 * i) / lumps;
      const rr = R * (0.92 + 0.08 * Math.sin(i * 2.1));
      const x = c + Math.cos(a) * rr;
      const y = c + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    fillOutlined(ctx, shade(COLORS.groundVoidPurple, 0.45), 6);
    // Depth gradient down to black
    const grad = ctx.createRadialGradient(c, c, R * 0.1, c, c, R * 0.85);
    grad.addColorStop(0, 'rgba(4, 2, 10, 1)');
    grad.addColorStop(0.75, 'rgba(8, 5, 16, 0.95)');
    grad.addColorStop(1, 'rgba(16, 11, 28, 0)');
    circle(ctx, c, c, R * 0.85);
    ctx.fillStyle = grad;
    ctx.fill();
    // Faint void specks inside
    ctx.fillStyle = 'rgba(155, 89, 208, 0.35)';
    for (const [sx, sy] of [[-0.3, -0.1], [0.2, 0.25], [-0.05, 0.4]] as const) {
      circle(ctx, c + sx * R, c + sy * R, 2);
      ctx.fill();
    }
    void h;
  },
};

/** Portal gate — spinning vortex, 4 frames. Transition gate after the boss. */
export const envPortalGate: ProceduralDef = {
  key: 'env/portal-gate',
  width: 192,
  height: 192,
  anchorX: 0.5,
  anchorY: 0.5,
  frames: 4,
  draw(ctx, w, h, frame) {
    const c = w / 2;
    const R = c - 12;
    const rot = (frame * Math.PI) / 2 / 2; // 45° per frame

    // Outer stone-metal ring
    circle(ctx, c, c, R);
    fillOutlined(ctx, 0x2a2440, 7);
    circle(ctx, c, c, R - 14);
    ctx.fillStyle = css(0x171226);
    ctx.fill();

    // Runic lights on the ring
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i + rot;
      circle(ctx, c + Math.cos(a) * (R - 7), c + Math.sin(a) * (R - 7), 3.5);
      ctx.fillStyle = css(COLORS.uiAccent);
      ctx.fill();
    }

    // Inner vortex — spinning spiral arms
    const inner = R - 18;
    for (let arm = 0; arm < 3; arm++) {
      ctx.beginPath();
      const a0 = rot * 2 + (arm * Math.PI * 2) / 3;
      for (let t = 0; t <= 1; t += 0.08) {
        const a = a0 + t * 2.6;
        const rr = inner * (1 - t * 0.85);
        const x = c + Math.cos(a) * rr;
        const y = c + Math.sin(a) * rr;
        if (t === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = arm === 0 ? css(COLORS.playerCyan) : 'rgba(155, 89, 208, 0.8)';
      ctx.lineWidth = 6 - arm;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    // Center glow
    circle(ctx, c, c, 8 + frame);
    ctx.fillStyle = 'rgba(235, 245, 255, 0.9)';
    ctx.fill();
    void h;
  },
};
